/**
 * Read-only list of vet-authored clinical records (finalized) for a pet.
 * Owner-facing surface for the C5 endpoint. Separate from the self-reported
 * vaccinations/allergies/conditions in PetMedicalSection.
 */
import { useState } from 'react';
import { ChevronDown, FileImage, FileText, Loader2, Stethoscope } from 'lucide-react';
import { PrescriptionDocGrid } from '@/components/prescription-doc-grid';
import { use_pet_medical_records } from '@/features/pet/hooks';
import type { MedicalRecord } from '@/features/medical-record/types';
import { date_label } from '@/lib/datetime';

function fmt_date(iso: string | null): string {
  if (!iso) return '—';
  return date_label(iso);
}

function RecordCard({ record }: { record: MedicalRecord }) {
  const [open, set_open] = useState(false);
  const items = record.prescription?.items ?? [];
  const docs = record.prescription_documents ?? [];

  return (
    <li className="rounded-input border-2 border-brand-outline-variant/20" data-testid={`vet-record-${record.id}`}>
      <button
        type="button"
        onClick={() => set_open((o) => !o)}
        className="flex w-full items-center gap-3 p-3 text-left"
        aria-expanded={open}
      >
        <Stethoscope className="h-4 w-4 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{record.diagnosis || 'Visit record'}</p>
          <p className="text-xs text-muted-foreground">
            {fmt_date(record.finalized_at ?? record.created_at)}
            {items.length > 0 && ` · ${items.length} medicine${items.length > 1 ? 's' : ''}`}
          </p>
        </div>
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="flex flex-col gap-3 border-t-2 border-brand-outline-variant/10 p-3 text-sm" data-testid={`vet-record-detail-${record.id}`}>
          {record.chief_complaint && (
            <Field label="Chief complaint" value={record.chief_complaint} />
          )}
          <Field label="Diagnosis" value={record.diagnosis} />
          {record.clinical_notes && <Field label="Notes" value={record.clinical_notes} />}
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
            {record.weight_kg && <span>Weight: {record.weight_kg} kg</span>}
            {record.follow_up_on && <span>Follow-up: {fmt_date(record.follow_up_on)}</span>}
          </div>

          {(items.length > 0 || record.prescription?.advice) && (
            <div className="rounded-input bg-secondary/40 p-2">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Prescription</p>
              {items.length > 0 ? (
                <ul className="flex flex-col gap-1.5">
                  {items.map((it) => (
                    <li key={it.id} className="text-sm">
                      <span className="font-medium">{it.drug_name}</span>
                      {it.strength && <span className="text-muted-foreground"> · {it.strength}</span>}
                      <span className="text-muted-foreground"> · {it.form.toLowerCase()}</span>
                      {(it.dosage || it.frequency || it.duration_days) && (
                        <span className="block text-xs text-muted-foreground">
                          {[it.dosage, it.frequency, it.duration_days ? `${it.duration_days} days` : null].filter(Boolean).join(' · ')}
                          {it.instructions ? ` — ${it.instructions}` : ''}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : null}
              {record.prescription?.advice && <p className="mt-2 text-xs text-muted-foreground">{record.prescription.advice}</p>}
            </div>
          )}

          {docs.length > 0 && (
            <div className="flex flex-col gap-1.5" data-testid={`vet-record-docs-${record.id}`}>
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <FileImage className="h-3.5 w-3.5" /> Prescription photos
              </p>
              <PrescriptionDocGrid docs={docs} size="sm" testid_prefix={`vet-record-doc-${record.id}`} />
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="whitespace-pre-wrap">{value}</p>
    </div>
  );
}

export function PetVetRecordsSection({ pet_id }: { pet_id: string }) {
  const records_q = use_pet_medical_records(pet_id);
  const records = records_q.data ?? [];

  return (
    <div className="flex flex-col gap-3" data-testid="pet-vet-records-section">
      <div className="flex items-center gap-2">
        <FileText className="h-4 w-4 text-primary" />
        <h3 className="font-heading text-base font-semibold">Vet visit records</h3>
      </div>

      {records_q.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : records.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="pet-vet-records-empty">
          No vet records yet. After a vet visit, your vet's notes and prescription appear here.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {records.map((r) => (
            <RecordCard key={r.id} record={r} />
          ))}
        </ul>
      )}
    </div>
  );
}
